import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../data/mockData';
import {
  Search,
  Gift,
  Store,
  Menu as MenuIcon,
  X,
  UserRound,
  Sparkles,
  ChevronRight,
  MapPin,
  Loader2,
  Users,
} from 'lucide-react';
import { NofeeNavLogo } from './NoFeeLogo';
import RestaurantAuthModal from './RestaurantAuthModal';
import CustomerAuthModal from './CustomerAuthModal';
import MyAccountDropdown from './MyAccountDropdown';
import RestaurantsDropdown from './RestaurantsDropdown';
import PartnersDropdown from './PartnersDropdown';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const Navbar = ({ forceSolid = false }) => {
  const { lang, setLang } = useLanguage();
  const { i18n, t } = useTranslation();
  const legacy_t = content[lang]?.nav || content.en.nav;
  const [isScrolled, setIsScrolled] = useState(false);
  const [zip, setZip] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [restAuthOpen, setRestAuthOpen] = useState(false);
  const [restAuthMode, setRestAuthMode] = useState('login');
  const [custAuthOpen, setCustAuthOpen] = useState(false);
  const [custAuthMode, setCustAuthMode] = useState('login');
  const navigate = useNavigate();
  const location = useLocation();

  // Active route detection (used to highlight the matching nav item and
  // de-emphasize unrelated CTAs like the gold "Find Restaurants" submit
  // button while the user is reading the Rewards marketing page).
  const isRewardsRoute =
    location.pathname === '/rewards' || location.pathname.startsWith('/rewards/');
  const isRestaurantsRoute =
    location.pathname === '/restaurants' || location.pathname.startsWith('/restaurants/');
  const isPartnersRoute =
    location.pathname === '/partners' || location.pathname.startsWith('/partners/');

  // One-account rule: detect existing NoFee customer session so we can swap
  // the public "Customer Login" entry for a personal "My Rewards" shortcut
  // that deep-links straight into their loyalty dashboard.
  const [customerToken, setCustomerToken] = useState(
    typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null
  );
  useEffect(() => {
    const sync = () => {
      try {
        setCustomerToken(localStorage.getItem('nofee_customer_token'));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', sync);
    // Re-check on route changes (login/logout typically navigates).
    sync();
    return () => window.removeEventListener('storage', sync);
  }, [location.pathname]);
  const isCustomerLoggedIn = Boolean(customerToken);

  const openRestaurantAuth = (mode = 'login') => {
    setRestAuthMode(mode);
    setRestAuthOpen(true);
    setDrawerOpen(false);
  };

  const openCustomerAuth = (mode = 'login') => {
    setCustAuthMode(mode);
    setCustAuthOpen(true);
    setDrawerOpen(false);
  };

  useEffect(() => {
    const sync = (lng) => {
      const next = ['en', 'es'].includes(lng) ? lng : 'en';
      if (next !== lang) setLang(next);
    };
    sync(i18n.language);
    i18n.on('languageChanged', sync);
    return () => i18n.off('languageChanged', sync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll + ESC to close
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    if (drawerOpen) window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    const term = (zip || '').trim();
    navigate(term ? `/restaurants?q=${encodeURIComponent(term)}` : '/restaurants');
  };

  // Geolocation auto-detect — fills the ZIP/city input with the customer's
  // current postal code via the browser Geolocation API + a free reverse
  // geocoding service (BigDataCloud, no API key required). Best-effort: any
  // failure (denied permission, network error, no zip in response) silently
  // falls back to manual entry — never blocks the user.
  const detectLocation = async () => {
    if (geoLoading || !navigator.geolocation) return;
    try {
      setGeoLoading(true);
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 300000,
        });
      });
      const { latitude, longitude } = pos.coords;
      const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const detected = data?.postcode || data?.city || data?.locality || '';
        if (detected) setZip(String(detected).slice(0, 60));
      }
    } catch {
      /* silent — manual entry remains available */
    } finally {
      setGeoLoading(false);
    }
  };

  const onMobileSearch = (e) => {
    e?.preventDefault?.();
    setDrawerOpen(false);
    onSearch();
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          forceSolid || isScrolled
            ? 'bg-black/95 backdrop-blur-md shadow-lg shadow-black/20 border-b border-gold-900/30'
            : 'bg-black/40 backdrop-blur-sm'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 lg:gap-5 h-16 lg:h-[72px]">
            {/* Logo (left) */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex-shrink-0 cursor-pointer"
              data-testid="navbar-logo"
              aria-label="NoFee home"
            >
              <NofeeNavLogo height={46} />
            </button>

            {/* Centered ZIP / address search bar (desktop only) */}
            <form
              onSubmit={onSearch}
              className="hidden md:flex flex-1 max-w-[640px] mx-auto items-center bg-white/[0.04] hover:bg-white/[0.06] focus-within:bg-white/[0.07] border border-white/10 focus-within:border-gold-500/50 rounded-2xl transition-all"
              data-testid="navbar-zip-search-form"
            >
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder={t('nav.search_placeholder')}
                  className="w-full bg-transparent pl-11 pr-10 py-2.5 text-[14px] text-white placeholder:text-white/45 focus:outline-none"
                  data-testid="navbar-zip-search-input"
                />
                <button
                  type="button"
                  onClick={detectLocation}
                  disabled={geoLoading}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg flex items-center justify-center text-white/55 hover:text-gold-300 hover:bg-white/10 transition-all disabled:opacity-60"
                  title="Use my location"
                  aria-label="Use my location"
                  data-testid="navbar-geolocate-btn"
                >
                  {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                </button>
              </div>
              <button
                type="submit"
                className={`m-1 px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${
                  isRewardsRoute
                    ? 'bg-white/[0.06] hover:bg-white/[0.10] border border-white/15 text-white/85 hover:text-white'
                    : 'bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black'
                }`}
                data-testid="navbar-find-restaurants-btn"
                aria-current={isRestaurantsRoute ? 'page' : undefined}
              >
                {t('nav.find')} {t('nav.restaurants')}
              </button>
            </form>

            {/* Push to the right on mobile (no centered search) */}
            <div className="md:hidden flex-1" />

            {/* Right side — desktop links + Get Started */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Restaurants dropdown — sells NoFee to potential restaurant
                  partners (benefits + commission math) before surfacing the
                  Sign In / Create Restaurant Account CTAs. */}
              <RestaurantsDropdown onOpenAuth={(mode) => openRestaurantAuth(mode || 'login')} />
              <a
                href="/rewards"
                className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all text-white/85 hover:text-gold-300"
                data-testid="navbar-rewards-link"
                aria-current={isRewardsRoute ? 'page' : undefined}
              >
                <Gift className="w-3.5 h-3.5" />
                {t('nav.rewards')}
              </a>
              <PartnersDropdown />
              {/* My Account — always renders the dropdown.  Logged-out
                  state shows a Welcome panel with Sign In + Create Account
                  CTAs; logged-in state shows the full account menu.        */}
              <MyAccountDropdown
                onSignOut={() => setCustomerToken(null)}
                onOpenAuth={(mode) => openCustomerAuth(mode || 'login')}
              />

              {/* Language switcher — visible on every customer-facing page
                  (Navbar is shared). Dark variant matches the black-and-gold
                  nav surface; compact mode hides the language name on mobile. */}
              <div className="hidden sm:inline-flex" data-testid="navbar-language-switcher">
                <LanguageSwitcher variant="dark" compact />
              </div>

              {/* Hamburger — mobile + tablet only (hidden on lg+) */}
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden ml-1 w-10 h-10 inline-flex items-center justify-center rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-gold-500/40 text-white/85 hover:text-gold-300 transition-all"
                aria-label="Open menu"
                aria-expanded={drawerOpen}
                data-testid="navbar-mobile-menu-btn"
              >
                <MenuIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mobile-only ZIP search row (kept under the bar for one-tap "Find") */}
          <div className="md:hidden pb-3">
            <form
              onSubmit={onSearch}
              className="flex items-center bg-white/[0.04] border border-white/10 rounded-2xl"
              data-testid="navbar-zip-search-form-mobile"
            >
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder={t('nav.search_placeholder')}
                  className="w-full bg-transparent pl-10 pr-9 py-2.5 text-sm text-white placeholder:text-white/45 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={detectLocation}
                  disabled={geoLoading}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg flex items-center justify-center text-white/55 hover:text-gold-300 disabled:opacity-60"
                  aria-label="Use my location"
                  data-testid="navbar-geolocate-btn-mobile"
                >
                  {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                </button>
              </div>
              <button
                type="submit"
                className={`m-1 px-3.5 py-2 rounded-xl text-sm font-bold whitespace-nowrap ${
                  isRewardsRoute
                    ? 'bg-white/[0.06] border border-white/15 text-white/85'
                    : 'bg-gradient-to-b from-amber-300 to-gold-500 text-black'
                }`}
              >
                Find
              </button>
            </form>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {drawerOpen && (
        <MobileDrawer
          onClose={() => setDrawerOpen(false)}
          onMobileSearch={onMobileSearch}
          zip={zip}
          setZip={setZip}
          getStartedLabel={legacy_t.getStarted}
          onOpenRestaurantAuth={openRestaurantAuth}
          onOpenCustomerAuth={openCustomerAuth}
          isCustomerLoggedIn={isCustomerLoggedIn}
          isRewardsRoute={isRewardsRoute}
          isPartnersRoute={isPartnersRoute}
        />
      )}

      {/* Restaurant access modal (Sign in + Create account) */}
      <RestaurantAuthModal
        open={restAuthOpen}
        initialMode={restAuthMode}
        onClose={() => setRestAuthOpen(false)}
      />

      {/* Customer access modal (Sign in + Create account) */}
      <CustomerAuthModal
        open={custAuthOpen}
        initialMode={custAuthMode}
        onClose={() => setCustAuthOpen(false)}
      />
    </>
  );
};

// --------------------------------------------------------------------------
// MobileDrawer — premium slide-in from the right with all navigation items
// --------------------------------------------------------------------------
const MobileDrawer = ({ onClose, onMobileSearch, zip, setZip, getStartedLabel, onOpenRestaurantAuth, onOpenCustomerAuth, isCustomerLoggedIn, isRewardsRoute, isPartnersRoute }) => {
  const { t } = useTranslation();
  const items = [
    { label: 'Restaurants',     onClick: () => onOpenRestaurantAuth('login'), icon: Store, group: 'primary' },
    { label: 'Rewards',         href: '/rewards',                              icon: Gift,  group: 'primary', active: isRewardsRoute },
    { label: 'Partners',        href: '/partners',                             icon: Users, group: 'primary', active: isPartnersRoute },
    { label: 'Find Restaurants',href: '/restaurants',                          icon: Search,group: 'primary' },
    isCustomerLoggedIn
      ? { label: 'My Account',  href: '/account',                              icon: UserRound, group: 'login', highlight: true }
      : { label: 'My Account',  onClick: () => onOpenCustomerAuth('login'),    icon: UserRound, group: 'login', highlight: true },
  ];

  return (
    <div
      className="lg:hidden fixed inset-0 z-[80]"
      role="dialog"
      aria-modal="true"
      data-testid="navbar-mobile-drawer"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      />

      {/* Drawer panel */}
      <aside
        className="absolute top-0 right-0 h-full w-[88%] max-w-[400px] bg-gradient-to-b from-zinc-950 via-black to-zinc-950 border-l border-gold-900/30 shadow-[0_0_60px_-10px_rgba(0,0,0,0.7)] flex flex-col"
        style={{ animation: 'nofee-drawer-slide-in 280ms cubic-bezier(0.22, 1, 0.36, 1) both' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/[0.06]">
          <NofeeNavLogo height={36} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            data-testid="navbar-mobile-drawer-close"
            className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 hover:text-white transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <form onSubmit={onMobileSearch} className="px-5 pt-5">
          <div className="flex items-center bg-white/[0.04] border border-white/10 focus-within:border-gold-500/50 rounded-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder={t('nav.search_placeholder')}
                className="w-full bg-transparent pl-11 pr-3 h-12 text-[14px] text-white placeholder:text-white/45 focus:outline-none"
                data-testid="navbar-mobile-drawer-search"
              />
            </div>
            <button
              type="submit"
              className="m-1 px-3.5 h-10 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 text-black text-sm font-bold whitespace-nowrap"
              data-testid="navbar-mobile-drawer-find"
            >
              Find
            </button>
          </div>
        </form>

        {/* Nav links — large tap targets */}
        <nav className="flex-1 overflow-y-auto px-3 pt-6 pb-4">
          <p className="px-3 text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-bold mb-2">
            Discover
          </p>
          {items.filter((i) => i.group === 'primary').map((it) => (
            <DrawerLink key={it.label} {...it} onClose={onClose} />
          ))}

          <p className="px-3 mt-6 text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-bold mb-2">
            Account
          </p>
          {items.filter((i) => i.group === 'login').map((it) => (
            <DrawerLink key={it.label} {...it} onClose={onClose} />
          ))}

          {/* Language — mobile users get the same switcher tucked into the
              drawer (where the sm:inline-flex copy on the topbar is hidden). */}
          <p className="px-3 mt-6 text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-bold mb-2">
            Language
          </p>
          <div className="px-3 py-2" data-testid="mobile-drawer-language">
            <LanguageSwitcher variant="dark" />
          </div>
        </nav>

        {/* Bottom CTA — opens the Restaurants modal directly */}
        <div className="px-5 pb-6 pt-3 border-t border-white/[0.06] bg-black/40">
          <button
            type="button"
            onClick={() => onOpenRestaurantAuth('signup')}
            data-testid="navbar-mobile-drawer-cta"
            className="w-full inline-flex items-center justify-center gap-2 h-[52px] rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[15px] shadow-[0_12px_30px_-10px_rgba(212,175,55,0.6)] transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Open a restaurant account
          </button>
          <p className="text-center text-[11px] text-white/40 mt-3">
            Free to start · 0% commission
          </p>
        </div>
      </aside>

      {/* Keyframe — inline so we don't need to touch index.css */}
      <style>{`
        @keyframes nofee-drawer-slide-in {
          0% { transform: translateX(100%); opacity: 0.6; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          aside[role] { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

const DrawerLink = ({ label, href, onClick, icon: Icon, onClose, active, highlight }) => {
  const testId = `navbar-mobile-drawer-link-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  const inner = (
    <>
      <span className="flex items-center gap-3.5">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
          active || highlight
            ? 'bg-yellow-500/15 border border-yellow-500/45'
            : 'bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40'
        }`}>
          <Icon className={`w-4 h-4 ${active || highlight ? 'text-yellow-300' : 'text-white/75 group-hover:text-gold-300'}`} />
        </span>
        <span className={`text-[15px] font-semibold ${active || highlight ? 'text-yellow-200' : ''}`}>{label}</span>
      </span>
      <ChevronRight className={`w-4 h-4 transition-all ${active || highlight ? 'text-yellow-300' : 'text-white/30 group-hover:text-gold-300 group-hover:translate-x-0.5'}`} />
    </>
  );
  const cls =
    `group w-full text-left flex items-center justify-between px-3 py-3.5 rounded-xl transition-all ${
      active || highlight
        ? 'bg-yellow-500/[0.06] text-yellow-200'
        : 'text-white/85 hover:text-gold-200 hover:bg-white/[0.04] active:bg-white/[0.06]'
    }`;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={() => {
          onClick();
          onClose?.();
        }}
        data-testid={testId}
        className={cls}
      >
        {inner}
      </button>
    );
  }
  return (
    <a href={href} onClick={onClose} data-testid={testId} className={cls}>
      {inner}
    </a>
  );
};

export default Navbar;
